import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { IconExternalLink, IconX } from '@/components/icons'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'

/** Узкий шрифт — больше текста в той же ширине */
const TAB_MAX_W = 264
const STRIP_PT = 6
const STRIP_PB = 6
function clampMask(
  stripW: number,
  left: number,
  width: number,
): { left: number; width: number } {
  let l = left
  let w = width
  if (l < 0) {
    w += l
    l = 0
  }
  if (l + w > stripW) w = stripW - l
  return { left: l, width: Math.max(0, w) }
}

export function WorkspaceTabBar() {
  const { tabs, activeTabId, selectTab, closeTab, openInBrowserTab } = useWorkspaceTabs()
  const stripRef = useRef<HTMLDivElement | null>(null)
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const activeElRef = useRef<HTMLDivElement | null>(null)
  const [mask, setMask] = useState({ left: 0, width: 0 })

  const updateMask = useCallback(() => {
    const strip = stripRef.current
    const tab = activeElRef.current
    if (!strip || !tab) {
      setMask({ left: 0, width: 0 })
      return
    }
    const sr = strip.getBoundingClientRect()
    const tr = tab.getBoundingClientRect()
    const next = clampMask(sr.width, tr.left - sr.left, tr.width)
    setMask(next)
  }, [])

  useLayoutEffect(() => {
    updateMask()
    const strip = stripRef.current
    const tab = activeElRef.current
    const vp = viewportRef.current
    if (!strip) return
    const ro = new ResizeObserver(() => updateMask())
    ro.observe(strip)
    if (tab) ro.observe(tab)
    window.addEventListener('resize', updateMask)
    vp?.addEventListener('scroll', updateMask, { passive: true })
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', updateMask)
      vp?.removeEventListener('scroll', updateMask)
    }
  }, [activeTabId, tabs.length, updateMask])

  useEffect(() => {
    activeElRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
    const t = window.setTimeout(updateMask, 120)
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(updateMask)
    })
    return () => {
      clearTimeout(t)
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
    }
  }, [activeTabId, updateMask])

  return (
    <Box
      ref={stripRef}
      style={{
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background:
          'linear-gradient(180deg, light-dark(rgba(248,250,252,0.96), rgba(26,27,30,0.92)), light-dark(rgba(241,245,249,0.86), rgba(20,21,24,0.92)))',
        borderBottom: '1px solid light-dark(rgba(148,163,184,0.24), rgba(255,255,255,0.07))',
      }}
    >
      <ScrollArea
        className="workspace-tab-scroll"
        viewportRef={viewportRef}
        type="scroll"
        scrollbars="x"
        offsetScrollbars="x"
        scrollbarSize={9}
        styles={{
          root: {
            width: '100%',
            maxWidth: '100%',
          },
          viewport: {
            paddingTop: STRIP_PT,
            paddingBottom: STRIP_PB,
          },
          scrollbar: {
            marginInline: 10,
            borderRadius: 999,
            background: 'light-dark(rgba(226,232,240,0.82), rgba(255,255,255,0.08))',
          },
          thumb: {
            borderRadius: 999,
            background: 'light-dark(rgba(37,99,235,0.46), rgba(96,165,250,0.56))',
          },
        }}
      >
        <Flex
          gap={6}
          align="center"
          wrap="nowrap"
          pl="sm"
          pr="sm"
          style={{
            width: 'max-content',
            minWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {tabs.map((tab) => {
            const active = tab.id === activeTabId
            return (
              <Flex
                key={tab.id}
                ref={active ? activeElRef : undefined}
                className={active ? undefined : 'workspace-tab-strip__idle'}
                role="tab"
                aria-selected={active}
                tabIndex={active ? 0 : -1}
                gap={0}
                align="center"
                wrap="nowrap"
                style={{
                  flexShrink: 0,
                  maxWidth: TAB_MAX_W,
                  minHeight: 34,
                  borderRadius: 'var(--mantine-radius-md)',
                  transition: 'background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
                  ...(active
                    ? {
                        position: 'relative',
                        zIndex: 2,
                        background:
                          'radial-gradient(circle at 100% 0, light-dark(rgba(59,130,246,0.12), rgba(59,130,246,0.16)), transparent 42%), light-dark(#ffffff, var(--mantine-color-dark-7))',
                        border: '1px solid var(--mantine-color-brand-outline)',
                        boxShadow: '0 10px 24px light-dark(rgba(37,99,235,0.11), rgba(0,0,0,0.22))',
                      }
                    : {
                        background: 'light-dark(rgba(255,255,255,0.58), rgba(37,38,43,0.58))',
                        border: '1px solid light-dark(rgba(148,163,184,0.18), rgba(255,255,255,0.06))',
                      }),
                }}
              >
                <UnstyledButton
                  type="button"
                  onClick={() => selectTab(tab.id)}
                  px={active ? 10 : 8}
                  py={active ? 5 : 4}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 'var(--mantine-radius-md)',
                  }}
                >
                  <Text
                    fz={13}
                    fw={active ? 600 : 500}
                    c={active ? undefined : 'dimmed'}
                    truncate
                    lh={1.25}
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {tab.title}
                  </Text>
                </UnstyledButton>
                <Group gap={0} wrap="nowrap" pr={2} style={{ flexShrink: 0 }}>
                  <Tooltip label="В новой вкладке браузера" position="bottom" withArrow>
                    <ActionIcon
                      variant={active ? 'subtle' : 'transparent'}
                      color="gray"
                      size="xs"
                      radius="md"
                      aria-label="Открыть в новой вкладке браузера"
                      onClick={(e) => {
                        e.stopPropagation()
                        openInBrowserTab(tab.id)
                      }}
                    >
                      <IconExternalLink size={14} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Закрыть вкладку" position="bottom" withArrow>
                    <ActionIcon
                      variant={active ? 'subtle' : 'transparent'}
                      color="gray"
                      size="xs"
                      radius="md"
                      aria-label="Закрыть вкладку"
                      disabled={tabs.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation()
                        closeTab(tab.id)
                      }}
                    >
                      <IconX size={14} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Flex>
            )
          })}
        </Flex>
      </ScrollArea>

      {/* Мягкий индикатор активной вкладки под strip — не клипится ScrollArea. */}
      {mask.width > 0 ? (
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            left: mask.left + 8,
            width: Math.max(0, mask.width - 16),
            bottom: 0,
            height: 3,
            borderRadius: 999,
            background: 'var(--mantine-color-brand-filled)',
            zIndex: 3,
            pointerEvents: 'none',
            transition: 'left 0.22s cubic-bezier(0.22, 1, 0.36, 1), width 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      ) : null}
    </Box>
  )
}
